// The old Studio (the canvas), kept during the transition to the three
// columns (StudioCentre.tsx; #315): shown only at a hand-typed
// `/classic/studio` or `/classic/projects/:id` (`frame.studioSurface`),
// linked from nowhere, removed when the owner retires it. It reads the same
// project state as the new Studio. Moved from StudioCentre.tsx unchanged
// but for the way into a thing: opening a card (or "Editor" on the mode
// switch) opens the new Studio at it, which replaced the old Editor mode.
// What it does (the canvas's own story):
//  - selection IS the composer's context: the selected cards are the
//    composer's mention chips (the same `@`-mention path, so removing a chip
//    deselects its card, and the next message carries them);
//  - the toolbar (pen B4): the container tool puts cards into a new module
//    (or a card menu adds them to one) and a new material starts empty, each
//    one command (useStudioCommands.ts) whose result is placed where it was
//    made; Source uploads; Menu shows or hides the app's left menu;
//  - a container's part rows move a part, or take it out, each with an Undo
//    (useStudioCommands.ts);
//  - the Export button sums up the project's issues (useProjectIssues.ts,
//    PS-54); a row of its list opens that thing in the new Studio;
//  - the view's state is one machine (studioMachine.ts; #256): a name is
//    renamed in place (MF-1), saved at once and taken back by the module
//    toolbar's Undo; this runs the machine's effects on the shell's doors;
//  - a running assistant turn is a working card (studioWork.ts), next to the
//    cards its message referenced;
//  - a card taken off the canvas stays in the Library (with an Undo); the
//    canvas menu puts any Library item not on this canvas onto it; "Delete
//    material …" asks first, through the Library's own confirmation;
//  - which containers are folded, and the view (zoom + centre), are this
//    person's, kept on this device; where each card lies is the project's.

import { exhaustSwitchCase } from "@bilda/utils";
import { type ReactNode, useRef, useState } from "react";
import type { ComposerResourceRef } from "../../../components/Composer/PowerComposer/types.js";
import { Studio } from "../../../components/Studio/Studio.js";
import { projectStudioCards } from "../../../components/Studio/studioCards.js";
import {
    type DeskCommand,
    openThingCommands,
} from "../../../components/Studio/studioDesk.js";
import {
    type Point,
    placeCards,
    type StudioLayout,
} from "../../../components/Studio/studioLayout.js";
import {
    initialStudioView,
    resolveStudioView,
    type StudioEffect,
    type StudioEvent,
    type StudioFacts,
    studioStep,
} from "../../../components/Studio/studioMachine.js";
import { projectStudioWork } from "../../../components/Studio/studioWork.js";
import { StudioModeSwitch } from "../../../components/StudioEditor/StudioModeSwitch.js";
import { editorEntry } from "../../../components/StudioEditor/studioMode.js";
import { StudioExport } from "../../../components/StudioExport/StudioExport.js";
import {
    readContainerKind,
    readFoldedContainers,
    readStudioView,
    writeContainerKind,
    writeFoldedContainers,
    writeStudioView,
} from "../../studioLayoutPreference.js";
import { toComposerResourceRef } from "../constants.js";
import { useAppShell } from "../context.js";
import { runPartIntent } from "./studioIntents.js";
import { useProjectIssues } from "./useProjectIssues.js";

export const ClassicStudioCentre = (): ReactNode => {
    const {
        library,
        searchResults,
        composerMentions,
        handleMentionsChange,
        refFor,
        dispatch,
        frame,
        health,
        studio,
        openFilePicker,
        chatState,
        turnFailureText,
        openConversation,
        submitText,
        openOverlay,
        setConfirmDelete,
        ports,
    } = useAppShell();
    const issues = useProjectIssues(ports, frame.studioProject, library);
    // Where a card was just dropped, until the project's layout shows it
    // (view state; the project's place wins once it differs no more).
    const [dropped, setDropped] = useState<ReadonlyMap<string, Point>>(
        new Map(),
    );
    // The view this person left the canvas at (zoom + centre), per device;
    // kept here too, so the canvas comes back to it from the Editor.
    const [storedView, setStoredView] = useState(readStudioView);
    // The Container tool's kind, remembered per device (pen B4).
    const [containerKind, setContainerKind] = useState(readContainerKind);
    // The Studio's view state (studioMachine.ts); the ref is the latest, for
    // an event that arrives after a save answered.
    const [machine, setMachine] = useState(initialStudioView);
    const latest = useRef(machine);
    // The containers this person folded, per device; only they fold or
    // unfold one (nothing folds itself).
    const [folded, setFolded] = useState(readFoldedContainers);

    // The project the author is in, and where each card lies on its canvas.
    const project = (library?.projects ?? []).find(
        (p) => p.id === frame.studioProject,
    );
    const canvas = new Map(
        (project?.canvas ?? []).map((item): [string, Point] => [
            item.target,
            { x: item.x, y: item.y },
        ]),
    );
    const layout: StudioLayout = {
        positions: new Map([...canvas, ...dropped]),
    };

    // Every card, the ones on this canvas, and the rest (the canvas menu
    // puts one on).
    const all = projectStudioCards(
        searchResults,
        library,
        new Set(canvas.keys()),
    );
    const cards = all.filter((c) => canvas.has(c.id));
    const offCanvas = all
        .filter((c) => !canvas.has(c.id))
        .map((c) => ({ id: c.id, title: c.title }));
    const placements = placeCards(cards, layout);
    const onCanvas = new Set(cards.map((c) => c.id));
    const selected = new Set(
        composerMentions.map((m) => m.id).filter((id) => onCanvas.has(id)),
    );
    // The mentions that are not cards (an `@`-picked package) stay put.
    const others = composerMentions.filter((m) => !onCanvas.has(m.id));
    const chipOf = (id: string): readonly ComposerResourceRef[] => {
        const ref = refFor(id);
        return ref === undefined ? [] : [toComposerResourceRef(ref)];
    };
    const selectedChips = composerMentions.filter((m) => selected.has(m.id));

    // The machine's facts, its state resolved against them, and its runner:
    // each event steps it, each effect goes out through a shell door.
    const facts: StudioFacts = {
        titles: new Map(cards.map((c) => [c.id, c.title])),
        selected,
    };
    const run = (effect: StudioEffect) => {
        switch (effect.type) {
            case "select_only":
                handleMentionsChange([...others, ...chipOf(effect.id)]);
                return;
            case "rename":
                studio.rename(effect.id, effect.title, effect.previous, () =>
                    send({
                        type: "rename_failed",
                        id: effect.id,
                        previous: effect.previous,
                    }),
                );
                return;
            default:
                exhaustSwitchCase(effect);
        }
    };
    const send = (event: StudioEvent) => {
        const step = studioStep(latest.current, event, facts);
        latest.current = step.state;
        setMachine(step.state);
        for (const effect of step.effects) run(effect);
    };

    // Into a thing: the new Studio, at it (it replaced the Editor mode).
    const openInStudio = (...commands: readonly DeskCommand[]) => {
        studio.onDesk(...commands);
        dispatch({ type: "open-studio" });
    };
    const modeSwitch = (
        <StudioModeSwitch
            mode="canvas"
            onCanvas={() => undefined}
            onEditor={() =>
                openInStudio(...editorEntry(studio.desk, cards, [...selected]))
            }
        />
    );
    // The export check (PS-54): a row opens its thing in the new Studio.
    const exportCheck = (
        <StudioExport
            projectTitle={project?.title ?? ""}
            issues={issues}
            online={health === "online"}
            onOpen={(id) => openInStudio(...openThingCommands(cards, id))}
        />
    );

    const place = (id: string, at: Point) => {
        setDropped((m) => new Map(m).set(id, at));
        studio.moveOnCanvas(id, at);
    };
    // A card onto this canvas (a new one, or one picked from the menu).
    const placeNew = (id: string, at: Point) => studio.placeOnCanvas(id, at);

    // A container just made: placed, then named in place.
    const made = (at: Point) => (id: string) => {
        placeNew(id, at);
        send({ type: "container_made", id });
    };
    const onPartIntent = runPartIntent(studio);

    return (
        <Studio
            cards={cards}
            placements={placements}
            selected={selected}
            projectTitle={project?.title ?? ""}
            onAllProjects={() => dispatch({ type: "open-projects" })}
            loading={library === null}
            onSelectOnly={(id) =>
                handleMentionsChange([...others, ...chipOf(id)])
            }
            onToggleSelected={(id) =>
                handleMentionsChange(
                    selected.has(id)
                        ? composerMentions.filter((m) => m.id !== id)
                        : [...composerMentions, ...chipOf(id)],
                )
            }
            onClearSelection={() => {
                if (selectedChips.length > 0) handleMentionsChange(others);
            }}
            // The same export sheet as the material's own Export, over the
            // canvas.
            onExport={(card) =>
                openOverlay({ type: "export", materialId: card.id })
            }
            onMove={place}
            onPutInNewModule={(ids, at) => studio.putInNewModule(ids, made(at))}
            onPutInNewCourse={(ids, at) => studio.putInNewCourse(ids, made(at))}
            onAddToContainer={studio.addToModule}
            onPartIntent={onPartIntent}
            machine={resolveStudioView(machine, facts)}
            onEvent={send}
            folded={folded}
            onToggleFold={(id) => {
                const next = folded.has(id)
                    ? new Set([...folded].filter((f) => f !== id))
                    : new Set([...folded, id]);
                setFolded(next);
                writeFoldedContainers(next);
            }}
            onRemoveFromCanvas={studio.removeFromCanvas}
            offCanvas={offCanvas}
            onPutBack={placeNew}
            // The same confirmation as the Library's Delete.
            onDelete={(card) => {
                const ref = refFor(card.id);
                if (ref !== undefined) setConfirmDelete(ref);
            }}
            revealed={studio.revealed}
            containerKind={containerKind}
            onContainerKindChange={(kind) => {
                setContainerKind(kind);
                writeContainerKind(kind);
            }}
            onUpload={openFilePicker}
            onNewMaterial={(kind, at) =>
                studio.newMaterial(kind, (id) => placeNew(id, at))
            }
            menuShown={frame.nav === "expanded"}
            onToggleMenu={() => dispatch({ type: "toggle-rail" })}
            online={health === "online"}
            // The assistant's turns as working cards (the chat's own running
            // state); what they make arrives with the Library's live refresh.
            work={projectStudioWork(
                chatState.sessions,
                placements,
                turnFailureText,
            )}
            onOpenWork={openConversation}
            onAsk={(text) => submitText(text)}
            storedView={storedView}
            onLeave={(view) => {
                writeStudioView(view);
                setStoredView(view);
            }}
            onOpen={openInStudio}
            modeSwitch={modeSwitch}
            exportCheck={exportCheck}
        />
    );
};
